import React, { useRef, type JSX } from 'react';

import Clear from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';

import type { RecordOf } from 'immutable';
import { List } from 'immutable';

import { useAppSelector, useAppStore } from 'hooks/redux';

import { accountsSelectors } from 'app-components/flux/accounts';
import { chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import type { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { customersSelectors } from 'app-components/flux/customers';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { projectsSelectors } from 'app-components/flux/projects';
import { getProjectById } from 'app-components/flux/projects/projectsSelectors';
import type { Tag, TagProps } from 'app-components/flux/tags/tagsTypes';
import { splitsHelpers, transactionsSelectors } from 'app-components/flux/transactions';
import type { SplitItemProps, SplitProps, TransactionProps } from 'app-components/flux/transactions/transactionsTypes';

import QAmountField from 'components/QAmountField';
import MuiTagsField from 'components/QuickenControls/TagsField/TagsField';
import UsageWithCategory from 'components/UsageWithCategory';
import ExclusionSettings from 'components/Transactions/SplitDialog/ExclusionSettings';
import NotesField from 'components/Transactions/SplitDialog/NotesField';

import ClientProjectSelect, { type OrnamentType, type ProjectSelect } from 'components/ClientProjectSelect';

import type { SplitDialogMode } from 'components/Transactions/SplitDialog/actions';
import { useStyles } from 'components/Transactions/SplitDialog/styles';

type Props = {
  splitItem: SplitItemProps;
  index: number;
  setNewSplitState: React.Dispatch<React.SetStateAction<RecordOf<SplitProps>>>;
  showError: boolean;
  accountId: string;
  removeSplit: (index: number) => () => void;
  updateSplitAmount: (index: number) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  baseTxn?: TransactionProps;
  disabledQBP?: boolean;
  disabledPayment?: boolean;
  mode?: SplitDialogMode;
  effectiveExclusionState: {
    isExcludedFromF2S: boolean;
    isExcludedFromReports: boolean;
  };
  ornamentType: OrnamentType;
  visibleSections: {
    clientProject: boolean;
    tags: boolean;
    exclusions: boolean;
    note: boolean;
  };
  showExclusions: () => void;
};

const SplitRow = ({
  splitItem,
  index,
  setNewSplitState,
  showError,
  accountId,
  removeSplit,
  updateSplitAmount,
  baseTxn,
  disabledQBP,
  disabledPayment,
  mode,
  effectiveExclusionState,
  ornamentType,
  visibleSections,
  showExclusions,
}: Props): JSX.Element => {
  const store = useAppStore();
  /** Same row chrome as series splits: header actions in parent, flex row layout, overflow treatment. */
  const usesUnifiedSplitChrome = mode === 'series' || mode === 'reminder' || mode === 'transaction';
  const { coa, amount } = splitItem;
  const coaRef = useRef<chartOfAccountsTypes.ChartOfAccountProps | null | undefined>(coa);
  const { classes, cx } = useStyles();

  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const [excludeIncomeByDefault] = usePreferenceV2('sSPExcludeIncomeByDefault', false);
  const incomeCoaIds = useAppSelector(categoriesSelectors.getIncomeCOAIds);
  const { businessId } = splitItem;
  const arAccountsByBusinessId = useAppSelector(accountsSelectors.getArAccountByBusiness);
  const accountsReceivable = arAccountsByBusinessId?.get(businessId ?? '');
  const isPayment = accountsReceivable && coa && coa?.id === accountsReceivable?.id;
  const isIncome = useAppSelector((state) =>
    splitItem.coa?.type === 'CATEGORY' ? Boolean(categoriesSelectors.isIncomeCat(state, splitItem.coa.id)) : false,
  );
  const expectedSign = isIncome ? 'positive' : 'negative';
  const getEffectiveSplitExclusionState = (nextSplitItem: SplitItemProps) =>
    transactionsSelectors.getSplitExclusionDisplayState(store.getState(), {
      split: nextSplitItem,
      parentIsExcludedFromF2S: baseTxn?.isExcludedFromF2S,
      parentIsExcludedFromReports: baseTxn?.isExcludedFromReports,
      transactionId: baseTxn?.id ?? undefined,
    });

  const isOverflow = splitsHelpers.isOverflowSplitItem(splitItem);
  const overflowAmountReadOnly = isOverflow && !splitsHelpers.isOverflowAmountEditable(baseTxn);

  const updateSplitTag = (idx: number, newTags: List<Tag | TagProps>) => {
    setNewSplitState((currentSplitState) => currentSplitState.setIn(['items', idx, 'tags'], newTags));
  };

  const updateSplitExclusion = (field: 'isExcludedFromF2S' | 'isExcludedFromReports') => (exclude: boolean) => {
    setNewSplitState((currentSplitState) => currentSplitState.setIn(['items', index, field], exclude));
  };

  const updateSplitMemo = (memo: string) => {
    setNewSplitState((currentSplitState) => currentSplitState.setIn(['items', index, 'memo'], memo));
  };

  const updateSplitUsageAndCategory = (val?: { usage: string; coa?: chartOfAccountsTypes.ChartOfAccountProps }) => {
    const { usage: newUsage = '', coa: newCoa } = val || {};
    setNewSplitState((currentSplitState) => {
      const currentSplitItemRecord = currentSplitState.getIn(['items', index]) as
        | (SplitItemProps & { toObject?: () => SplitItemProps })
        | undefined;
      const currentSplitItem = currentSplitItemRecord?.toObject?.() ?? currentSplitItemRecord ?? splitItem;
      const resetClientProject =
        Boolean(
          currentSplitItem.projectId &&
            getProjectById(store.getState(), currentSplitItem.projectId)?.businessId !== newUsage,
        ) || newUsage === 'PERSONAL';
      const nextSplitItemBase = {
        ...currentSplitItem,
        coa: newCoa,
        businessId: newUsage !== 'PERSONAL' ? newUsage : '',
        customerId: resetClientProject ? '' : currentSplitItem.customerId,
        projectId: resetClientProject ? '' : currentSplitItem.projectId,
        isBillable: resetClientProject ? false : currentSplitItem.isBillable,
      };
      const didCoaChange =
        (newCoa?.id ?? null) !== (currentSplitItem.coa?.id ?? null) || newCoa?.type !== currentSplitItem.coa?.type;
      const shouldApplyIncomeCategoryExclusionDefault =
        mode === 'transaction' &&
        Boolean(excludeIncomeByDefault) &&
        newCoa?.type === 'CATEGORY' &&
        Boolean(incomeCoaIds?.has?.(newCoa.id));
      const nextSplitItem = {
        ...nextSplitItemBase,
        ...(didCoaChange
          ? splitsHelpers.resolveSplitItemExclusions({
              splitItem: nextSplitItemBase,
              mode: mode ?? 'transaction',
              state: store.getState(),
              phase: 'categoryChange',
              parentExclusions: {
                isExcludedFromF2S: baseTxn?.isExcludedFromF2S,
                isExcludedFromReports: baseTxn?.isExcludedFromReports,
              },
            })
          : {}),
        // Transaction split rows follow the regular income-category default even when
        // the parent transaction started from the manual-review income flow.
        ...(didCoaChange && shouldApplyIncomeCategoryExclusionDefault
          ? {
              isExcludedFromF2S: true,
              isExcludedFromReports: false,
            }
          : {}),
      };
      const nextSplit = currentSplitState.mergeIn(['items', index], nextSplitItem);
      const nextEffectiveExclusionState = getEffectiveSplitExclusionState(nextSplitItem);
      if (
        !visibleSections.exclusions &&
        (Boolean(nextEffectiveExclusionState.isExcludedFromF2S) ||
          Boolean(nextEffectiveExclusionState.isExcludedFromReports) ||
          Boolean(baseTxn?.isExcludedFromF2S) ||
          Boolean(baseTxn?.isExcludedFromReports))
      ) {
        showExclusions();
      }
      coaRef.current = newCoa;
      return nextSplit;
    });
  };

  const setCustomersAndProjects = (res: ProjectSelect) => {
    setNewSplitState((currentSplitState) => {
      let nextSplit = currentSplitState;
      const currentSplitItemRecord = currentSplitState.getIn(['items', index]) as
        | (SplitItemProps & { toObject?: () => SplitItemProps })
        | undefined;
      const currentSplitItem = currentSplitItemRecord?.toObject?.() ?? currentSplitItemRecord ?? splitItem;
      const currentBusinessId = currentSplitItem.businessId;
      const currentCoa = currentSplitItem.coa;

      // Update coa field
      if (currentBusinessId && ornamentType === 'payment') {
        const businessArAccount = arAccountsByBusinessId.get(currentBusinessId);
        if (businessArAccount && res.payment) {
          coaRef.current = currentCoa;
          nextSplit = nextSplit.setIn(['items', index, 'coa'], { id: businessArAccount.id, type: 'ACCOUNT' });
        } else if (coaRef.current?.id === businessArAccount?.id) {
          nextSplit = nextSplit.setIn(['items', index, 'coa'], { id: '0', type: 'UNCATEGORIZED' });
        } else {
          nextSplit = nextSplit.setIn(['items', index, 'coa'], coaRef.current);
        }
      }

      // Update usage type to match default of client.
      if (res.customerId) {
        const customer = customersSelectors.getCustomerById(store.getState(), res.customerId);
        const project = res.projectId ? projectsSelectors.getProjectById(store.getState(), res.projectId) : null;
        const newBizId = project?.businessId || customer?.businessId;
        if (newBizId) {
          const category = currentCoa?.id ? categoriesSelectors.getCategoryById(store.getState(), currentCoa.id) : null;
          nextSplit = nextSplit.setIn(['items', index, 'businessId'], newBizId);
          if (category?.usageType !== 'BUSINESS' && !res.payment) {
            nextSplit = nextSplit.setIn(['items', index, 'coa'], { id: '0', type: 'UNCATEGORIZED' });
          }
        }
      }

      // Update customer project field
      nextSplit = nextSplit.mergeIn(['items', index], {
        isBillable: res.billable,
        customerId: res.customerId,
        projectId: res.projectId,
        payment: res.payment,
      });

      const nextCoa = nextSplit.getIn(['items', index, 'coa']) as
        | chartOfAccountsTypes.ChartOfAccountProps
        | null
        | undefined;
      const didCoaChange =
        (nextCoa?.id ?? null) !== (currentSplitItem.coa?.id ?? null) || nextCoa?.type !== currentSplitItem.coa?.type;

      if (didCoaChange) {
        const nextSplitItemRecord = nextSplit.getIn(['items', index]) as
          | (SplitItemProps & { toObject?: () => SplitItemProps })
          | undefined;

        if (nextSplitItemRecord) {
          const nextSplitItem = nextSplitItemRecord.toObject?.() ?? nextSplitItemRecord;
          nextSplit = nextSplit.mergeIn(['items', index], {
            ...splitsHelpers.resolveSplitItemExclusions({
              splitItem: nextSplitItem,
              mode: mode ?? 'transaction',
              state: store.getState(),
              phase: 'categoryChange',
              parentExclusions: {
                isExcludedFromF2S: baseTxn?.isExcludedFromF2S,
                isExcludedFromReports: baseTxn?.isExcludedFromReports,
              },
            }),
          });
        }
      }

      const nextSplitItemRecord = nextSplit.getIn(['items', index]) as
        | (SplitItemProps & { toObject?: () => SplitItemProps })
        | undefined;
      const nextSplitItem = nextSplitItemRecord?.toObject?.() ?? nextSplitItemRecord;

      if (nextSplitItem) {
        const nextEffectiveExclusionState = getEffectiveSplitExclusionState(nextSplitItem);
        if (
          !visibleSections.exclusions &&
          (Boolean(nextEffectiveExclusionState.isExcludedFromF2S) ||
            Boolean(nextEffectiveExclusionState.isExcludedFromReports) ||
            Boolean(baseTxn?.isExcludedFromF2S) ||
            Boolean(baseTxn?.isExcludedFromReports))
        ) {
          showExclusions();
        }
      }

      return nextSplit;
    });
  };

  const tagsValue = Array.isArray(splitItem.tags) ? splitItem.tags : splitItem.tags?.toArray() || [];

  return (
    <div key={`${splitItem.coa?.type}+${splitItem.coa?.id}+${index}`} className={classes.splitRowAttributes}>
      <div className={`${classes.catRow} ${usesUnifiedSplitChrome ? classes.catRowSeries : ''}`}>
        <UsageWithCategory
          accountId={accountId}
          size='small'
          categoryProps={{
            margin: 'normal',
            textFieldVariant: 'filled',
            variant: 'body1',
            label: 'Category',
            editable: true,
            autoComplete: 'off',
            name: 'txn-details',
            id: `split-cat-${index}`,
            value: splitItem.coa,
            classes: { root: `${classes.splitCat} ${isOverflow ? classes.splitCatOverflow : ''}` },
            createEnabled: true,
            allowBlank: true,
            filterFn: isOverflow
              ? (candidateCoa: chartOfAccountsTypes.ChartOfAccountProps) =>
                  Boolean(candidateCoa) && !chartOfAccountsUtils.isTransferCOA(candidateCoa)
              : undefined,
            error: showError && splitItem.coa === null,
            helperText: showError && splitItem.coa === null ? 'Category required' : null,
          }}
          onChange={updateSplitUsageAndCategory}
          disabled={disabledQBP}
          disabledPayment={disabledPayment}
          usageProps={{
            value: splitItem.businessId,
            cellId: `split-usage-${index}`,
          }}
        />

        {isOverflow && !splitsHelpers.shouldDisplayOverflowAmount(splitItem) ? (
          <TextField
            label='Amount'
            margin='normal'
            size='small'
            variant='filled'
            value='Overflow'
            id={`split-overflow-${index}`}
            className={`${classes.splitAmount} ${classes.splitAmountOverflowSpan} ${
              usesUnifiedSplitChrome ? classes.splitAmountOverflowSeriesSpan : ''
            } ${usesUnifiedSplitChrome ? classes.splitAmountSeriesOverflow : ''}`}
            InputProps={{ readOnly: true }}
            helperText={usesUnifiedSplitChrome ? undefined : 'Any extra amount is assigned to this split.'}
          />
        ) : (
          <QAmountField
            key={`${index}-${splitItem.coa?.type ?? 'none'}-${splitItem.coa?.id ?? 'none'}-${expectedSign}`}
            label='Amount'
            margin='normal'
            size='small'
            textFieldVariant='filled'
            editable
            value={amount}
            onChange={updateSplitAmount(index)}
            className={`${classes.splitAmount} ${usesUnifiedSplitChrome ? classes.splitAmountSeries : ''}`}
            inputId={isOverflow ? `split-overflow-${index}` : `split-amount-${index}`}
            // Only show green for positive values; keep negative values default (black).
            showColor='positive'
            expectedSign={expectedSign}
            error={!isOverflow && showError && splitItem.amount === 0}
            disabled={disabledQBP}
            helperText={!isOverflow && showError && splitItem.amount === 0 && 'Amount required'}
            {...(overflowAmountReadOnly ? { InputProps: { readOnly: true } } : {})}
          />
        )}

        {!isOverflow && !usesUnifiedSplitChrome && (
          <IconButton
            onClick={removeSplit(index)}
            id={`split-remove-${index}`}
            aria-label='Remove split'
            className={classes.splitRemove}
            disabled={disabledQBP}
            size='small'
            sx={{
              p: 0,
              width: 24,
              height: 24,
              '& .MuiSvgIcon-root': { fontSize: 24 },
            }}
          >
            <Clear />
          </IconButton>
        )}
      </div>

      {businessEnabled && visibleSections.clientProject && (
        <div id={`split-client-project-${index}-section`} className={classes.clientProjectSelect}>
          <ClientProjectSelect
            id={`split-client-project-${index}`}
            size='small'
            value={{
              projectId: splitItem.projectId ?? null,
              customerId: splitItem.customerId ?? null,
              billable: splitItem?.isBillable ?? false,
              payment: Boolean(isPayment),
            }}
            invoiceId={splitItem?.linkedItem?.id ?? ''}
            paymentAssetId={baseTxn?.id ?? ''}
            ornamentType={ornamentType ?? 'billable'}
            onChange={setCustomersAndProjects}
            disabledSelection={disabledQBP}
            disabledInvoicePayment={disabledPayment}
            splitId={splitItem?.id ?? undefined}
          />
        </div>
      )}

      {visibleSections.tags ? (
        <div className={classes.tagsRow}>
          <MuiTagsField
            id={`split-tags-${index}`}
            value={tagsValue}
            classes={{ root: classes.splitTag }}
            slotProps={{
              textFieldProps: {
                size: 'small',
                margin: 'none',
                hiddenLabel: true,
                placeholder: 'Tags',
                label: undefined,
              },
            }}
            onChange={(_e, tagArray: Array<Tag | TagProps>) => updateSplitTag(index, List(tagArray))}
          />
        </div>
      ) : null}

      {visibleSections.note || visibleSections.exclusions ? (
        <div className={cx(classes.splitMetaRow, usesUnifiedSplitChrome && classes.splitMetaRowSeries)}>
          {visibleSections.exclusions ? (
            <ExclusionSettings
              className={classes.splitExclusionSettings}
              id={`split-exclusions-${index}`}
              excludeReports={effectiveExclusionState.isExcludedFromReports}
              excludeF2S={effectiveExclusionState.isExcludedFromF2S}
              setExcludeReports={updateSplitExclusion('isExcludedFromReports')}
              setExcludeF2S={updateSplitExclusion('isExcludedFromF2S')}
              disabled={disabledQBP}
            />
          ) : null}
          {visibleSections.note ? (
            <NotesField
              className={classes.splitNotesField}
              id={`split-note-${index}`}
              value={splitItem.memo}
              onChange={updateSplitMemo}
              multiline={usesUnifiedSplitChrome}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default SplitRow;
